import { isoDate, type ISODate } from "@visioni/a3s-codecs";
import { DateTime } from "luxon";

export { isoDate, type ISODate };

export interface LocalDayBounds {
  readonly startsAt: Date;
  readonly endsAt: Date;
}

/**
 * The instants bounding `day` at `timezone`, half-open.
 *
 * Both ends are resolved from the calendar date rather than by adding 24 hours to the start: across
 * a DST transition a local day is 23 or 25 hours, and adding a fixed day would leave a gap or an
 * overlap between consecutive days.
 */
export function localDayBounds(day: string, timezone: string): LocalDayBounds {
  const start = DateTime.fromISO(isoDate.parse(day), { zone: timezone });
  if (!start.isValid) {
    throw new Error(`cannot resolve "${day}" in timezone "${timezone}": ${start.invalidReason}`);
  }
  return {
    startsAt: start.toJSDate(),
    endsAt: start.plus({ days: 1 }).toJSDate(),
  };
}

/**
 * Add `delta` whole calendar days to `day`, returning `YYYY-MM-DD`. Anchored at UTC midnight and
 * diffed in whole days, so it is a pure calendar-date label operation — no timezone is involved.
 */
export function addDays(day: string, delta: number): ISODate {
  const next = DateTime.fromISO(isoDate.parse(day), { zone: "UTC" }).plus({ days: delta }).toISODate();
  if (next === null) throw new Error(`cannot add ${String(delta)} days to "${day}"`);
  return isoDate.parse(next);
}

/** The calendar date `at` falls on, in `timezone`. */
export function localDateOf(at: Date, timezone: string): ISODate {
  const zoned = DateTime.fromJSDate(at, { zone: timezone });
  if (!zoned.isValid) {
    throw new Error(
      `cannot resolve the local date of "${at.toISOString()}" in timezone "${timezone}": ${zoned.invalidReason}`,
    );
  }
  return isoDate.parse(zoned.toISODate());
}

/** An ISO weekday: Monday = 1 … Sunday = 7. */
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * The ISO weekday (Monday = 1 … Sunday = 7) of a `YYYY-MM-DD` calendar date. A pure calendar-date
 * operation, like {@link addDays}: no timezone is involved. Throws on anything but `YYYY-MM-DD`.
 */
export function isoWeekdayOf(day: string): IsoWeekday {
  const date = DateTime.fromISO(isoDate.parse(day), { zone: "UTC" });
  if (!date.isValid) throw new Error(`cannot resolve the weekday of "${day}": ${date.invalidReason}`);
  return date.weekday;
}
